<script>
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';
import { formatMoney } from '~/javascripts/lib/format_money';
import { newSubscriptionPayload } from '~/javascripts/subscription_payloads';
import { purchaseSubscription } from '~/javascripts/api';
import TransactionCard from '~/javascripts/purchase/components/transaction_card.vue';
import PurchaseSubscriptionDetails from '~/javascripts/purchase/components/purchase_subscription_details.vue';
import PurchaseConfirmationCard from '~/javascripts/purchase/components/purchase_confirmation_card_deprecated.vue';
import PaymentMethodsSelect from '~/javascripts/purchase/shared/components/payment_methods_select.vue';
import TransactionLineItem from '~/javascripts/purchase/components/transaction_line_item.vue';
import CustomerInformation from '~/javascripts/subscription/components/customer_information.vue';
import PromotionalOfferAlert from '~/javascripts/subscription/components/promotional_offer_alert.vue';
import PromoCodeInput from '~/javascripts/subscription/components/promo_code_input.vue';
import { toLocaleDateString } from '~/javascripts/lib/datetime';
import Tracking from '~/javascripts/tracking';
import {
  INVALID_PROMO_CODE_ERROR_MESSAGE,
  PROMO_CODE_QUANTITY_ERROR_MESSAGE,
} from '~/javascripts/subscription/constants';
import { NEW_CREDIT_CARD_VALUE } from '~/javascripts/purchase/constants';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES } from '~/javascripts/tracking/constants';
import {
  isInvalidPromoCodeError,
  getChargeItem,
  getDiscountItem,
} from '~/javascripts/subscription/utils';
import { trackTransaction } from '~/javascripts/google_tag_manager';

export default {
  name: 'NewEESubscriptionForm',
  components: {
    PromoCodeInput,
    PurchaseSubscriptionDetails,
    PurchaseConfirmationCard,
    PaymentMethodsSelect,
    TransactionCard,
    TransactionLineItem,
    CustomerInformation,
    PromotionalOfferAlert,
  },
  inject: ['eligibleToUsePromoCode', 'planCode', 'showPromotionalOfferText', 'promoCode'],
  props: {
    seatPrice: {
      type: Number,
      required: true,
    },
    planId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isComplete: false,
      selectedPaymentMethodId: null,
      invoice: undefined,
      invoiceItem: undefined,
      serviceStartDate: undefined,
      serviceEndDate: undefined,
      isLoading: false,
      quantity: this.promoCode ? 1 : null, // Default to 1 when promo code is present
      isQuantityInvalid: !this.promoCode, // Valid quantity if promo code is present
      promoCode: this.promoCode, // Initialize from URL parameter
      promoCodeErrorMessage: undefined,
      applyingPromoCode: false,
      idempotencyKeys: {},
    };
  },
  computed: {
    total() {
      return this.invoice?.amount ?? 0;
    },
    subTotal() {
      return this.chargeItem?.charge_amount ?? 0;
    },
    discount() {
      return this.discountItem?.charge_amount ?? 0;
    },
    unitPrice() {
      return this.chargeItem?.unit_price ?? 0;
    },
    tax() {
      return this.invoice?.tax_amount ?? 0;
    },
    formattedSubTotal() {
      return formatMoney(this.subTotal, !this.hasValidPriceDetails);
    },
    formattedDiscount() {
      return formatMoney(this.discount, !this.hasValidPriceDetails);
    },
    formattedTax() {
      return formatMoney(this.tax, !this.hasValidPriceDetails);
    },
    localTotal() {
      return this.seatPrice * this.quantity;
    },
    purchaseButtonDisabled() {
      return (
        this.isLoading ||
        this.isComplete ||
        !this.selectedPaymentMethodId ||
        !this.hasValidPriceDetails
      );
    },
    subscriptionDetailsTitle() {
      if (!this.serviceStartDate || !this.serviceEndDate) {
        return '';
      }
      return `New subscription for ${toLocaleDateString(
        this.serviceStartDate,
      )} - ${toLocaleDateString(this.serviceEndDate)}`;
    },
    hasValidPriceDetails() {
      return Boolean(this.invoice) && !this.isQuantityInvalid;
    },
    chargeItem() {
      return getChargeItem(this.invoiceItem);
    },
    discountItem() {
      return getDiscountItem(this.invoiceItem);
    },
    hasDiscount() {
      return Boolean(this.discountItem);
    },
    showPromoCodeSuccessAlert() {
      return this.hasValidPriceDetails && this.hasDiscount;
    },
    idempotencyKeyParams() {
      return {
        planId: this.planId,
        quantity: this.quantity,
        selectedPaymentMethodId: this.selectedPaymentMethodId,
      };
    },
    serializedIdempotencyKeyParams() {
      return JSON.stringify(this.idempotencyKeyParams);
    },
    trackingLabel() {
      return `buy_${this.planCode}_sm`;
    },
  },
  watch: {
    quantity() {
      this.previewTransaction();
    },
    idempotencyKeyParams() {
      this.updateIdempotencyKey();
    },
  },
  created() {
    this.tracking = new Tracking();
    this.updateIdempotencyKey();
  },
  mounted() {
    // Auto-apply promo code if present in URL
    if (this.promoCode && this.eligibleToUsePromoCode) {
      this.applyPromoCode(this.promoCode);
    }
  },
  methods: {
    paymentMethodClick(paymentMethodId) {
      if (paymentMethodId === NEW_CREDIT_CARD_VALUE) {
        this.tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.addNewCard);
      }
      if (window.gon.global?.jh) {
        this.selectedPaymentMethodId = null;
      } else {
        this.selectedPaymentMethodId = paymentMethodId;
      }
    },
    clearError() {
      this.promoCodeErrorMessage = '';
      this.$emit('error', null);
    },
    getPaymentMethodIdParam({ isConfirmed }) {
      if (!isConfirmed) {
        return undefined;
      }
      return this.selectedPaymentMethodId;
    },
    getRequestParams({ isConfirmed }) {
      return newSubscriptionPayload({
        planId: this.planId,
        orderTotal: this.localTotal,
        totalWithoutTaxes: this.localTotal,
        seats: this.quantity,
        isConfirmed,
        paymentMethodId: this.getPaymentMethodIdParam({ isConfirmed }),
        promoCode: this.promoCode,
        idempotencyKey: this.idempotencyKeys[this.serializedIdempotencyKeyParams],
      });
    },
    handleQuantityChange(userQuantity) {
      this.quantity = userQuantity;
      this.isQuantityInvalid = false;

      this.tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.changeActiveUser, {
        value: this.quantity,
      });
    },
    handleInvalidQuantity() {
      this.isQuantityInvalid = true;
    },
    previewTransaction() {
      if (this.isQuantityInvalid) {
        return;
      }
      this.isLoading = true;
      const requestParams = this.getRequestParams({ isConfirmed: false });

      this.clearError();

      purchaseSubscription(requestParams)
        .then(({ invoice, invoice_item: invoiceItem }) => {
          this.invoice = invoice;
          this.invoiceItem = invoiceItem;
          this.serviceStartDate = this.chargeItem?.service_start_date;
          this.serviceEndDate = this.chargeItem?.service_end_date;

          if (this.hasDiscount && this.applyingPromoCode) {
            this.trackEvent(TRACKING_ACTIONS.promoCodeSuccessResponse);
          }
        })
        .catch((error) => {
          this.resetInvoiceDetails();
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.applyingPromoCode = false;
        });
    },
    makePayment() {
      this.isLoading = true;
      this.clearError();

      const requestParams = this.getRequestParams({ isConfirmed: true });

      return purchaseSubscription(requestParams)
        .then(this.handleSuccess)
        .catch(this.handlePurchaseError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleSuccess(response) {
      if (response.success !== true) {
        throw response.errors;
      }

      this.isComplete = true;

      this.tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.purchaseSubscription);

      this.trackTransaction(response.payment_method_id);

      const successResult = { subscriptionId: response.subscription_number };

      this.$emit('success', successResult);
    },
    trackTransaction(paymentMethodId) {
      const revenue = this.invoice.amount_without_tax ?? 0;
      const transactionDetails = {
        paymentOption: paymentMethodId,
        revenue,
        tax: this.tax,
        price: this.unitPrice,
        planId: this.planId,
        quantity: this.quantity,
      };

      trackTransaction(transactionDetails);
    },
    handlePurchaseError(error = {}) {
      const { status } = error;
      // Regenerate the idempotency key on client-side error responses (4xx) to ensure the server regards the new request.
      // Context: https://gitlab.com/gitlab-org/gitlab/-/merge_requests/129830#note_1522796835.
      if (status && status >= 400 && status < 500) {
        this.regenerateIdempotencyKey();
      }

      this.handleError(error);
    },
    handleError(error) {
      if (isInvalidPromoCodeError(error)) {
        this.promoCodeErrorMessage = INVALID_PROMO_CODE_ERROR_MESSAGE;
        this.promoCode = undefined;
        this.trackEvent(TRACKING_ACTIONS.promoCodeFailureResponse);
      } else {
        this.$emit('error', error);
        this.reportError(error);
      }
    },
    trackEvent(action) {
      this.tracking.event(TRACKING_CATEGORIES.webstore, action, { label: this.trackingLabel });
    },
    reportError(error) {
      Sentry.captureException(error, {
        tags: {
          vue_component: this.$options.name,
        },
      });
    },
    resetInvoiceDetails() {
      this.invoice = null;
      this.invoiceItem = null;
    },
    applyPromoCode(promoCode) {
      if (this.isQuantityInvalid) {
        this.promoCodeErrorMessage = PROMO_CODE_QUANTITY_ERROR_MESSAGE;
      } else {
        this.promoCode = promoCode;
        this.applyingPromoCode = true;
        this.previewTransaction();
        this.trackEvent(TRACKING_ACTIONS.clickApplyPromoCodeButton);
      }
    },
    updateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] =
        this.idempotencyKeys[this.serializedIdempotencyKeyParams] ?? uuidv4();
    },
    regenerateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] = uuidv4();
    },
  },
};
</script>
<template>
  <div class="row gl-mb-3 gl-justify-between">
    <transaction-card title="Subscription details" container-classes="col-md-12 col-lg-8">
      <purchase-subscription-details
        :title="subscriptionDetailsTitle"
        :is-loading="isLoading"
        :initial-min-quantity-count="1"
        :purchase-quantity="quantity"
        purchase-quantity-label="Active users"
        :unit-price="seatPrice"
        unit-description="per user annually"
        :sub-total="subTotal"
        sub-total-label="Subtotal for the new subscription"
        data-testid="active-users"
        @quantity-change="handleQuantityChange"
        @on-invalid-quantity="handleInvalidQuantity"
      >
        <template #discount-message>
          <promotional-offer-alert v-if="showPromotionalOfferText" />
        </template>

        <template v-if="eligibleToUsePromoCode" #promo-code>
          <promo-code-input
            :initial-promo-code="promoCode"
            :show-success-alert="showPromoCodeSuccessAlert"
            :is-parent-form-loading="isLoading"
            :is-applying-promo-code="applyingPromoCode"
            :error-message="promoCodeErrorMessage"
            @promo-code-submit="applyPromoCode"
          />
        </template>
      </purchase-subscription-details>

      <customer-information class="gl-border-t-1 gl-border-gray-100 gl-pt-5 gl-border-t-solid" />

      <transaction-line-item
        :is-loading="isLoading"
        label="Subtotal"
        :value="formattedSubTotal"
        data-testid="transaction-subtotal"
      />

      <transaction-line-item
        v-if="hasDiscount"
        :is-loading="isLoading"
        label="Discount"
        :value="formattedDiscount"
        :show-as-header="false"
        :top-border-enabled="false"
        data-testid="transaction-discount"
      />

      <transaction-line-item
        :is-loading="isLoading"
        label="Estimated taxes"
        :value="formattedTax"
        data-testid="transaction-tax"
      />
    </transaction-card>

    <purchase-confirmation-card
      card-title="Billing information"
      confirm-text="Buy subscription"
      :disabled="purchaseButtonDisabled"
      :loading="isLoading"
      :total="total"
      :is-invalid="!hasValidPriceDetails"
      @confirmPurchase="makePayment"
    >
      <template #payment-form>
        <payment-methods-select @click="paymentMethodClick" />
      </template>
    </purchase-confirmation-card>
  </div>
</template>
